<template>
    <div ref="autocompleteContainer" class="CFormAutocomplete">
        <!-- Error Message -->
        <p v-if="errorMessage" class="text-sm text-red-500 mb-2">{{ errorMessage }}</p>
        
        <div class="relative">
            <!-- Main Input Container -->
            <div
                ref="inputContainer"
                class="input flex-start fancy-scrollbar flex max-h-28 min-h-12 overflow-y-auto pl-1 pr-12 cursor-text"
                @click="focusInput"
            >
                <div class="flex max-w-full flex-auto flex-wrap items-center p-0.5">
                    <!-- Selected Interest Tags -->
                    <div
                        v-for="interest in selectedInterests"
                        :key="interest.value"
                        class="m-0.5 inline-flex max-w-full min-w-0 flex-none items-center overflow-hidden rounded-lg bg-gray-200 px-3 py-1.5 dark:bg-subtle"
                        :title="interest.label"
                    >
                        <slot name="selectedItem" :item="interest">
                            <div
                                v-if="permanent.includes(interest.value)"
                                class="flex min-w-0 items-center justify-center gap-2 overflow-hidden text-ellipsis"
                            >
                                <CIcon class="stroke-green-500 stroke-2 text-green-500" name="checkmark" size="small" />
                                <span class="truncate text-sm">{{ interest.label }}</span>
                            </div>
                            <div v-else class="flex min-w-0 items-center gap-1 overflow-hidden text-ellipsis text-sm">
                                <CIcon class="shrink-0 text-gray-600 dark:text-txt-body" name="hashtag" size="small" />
                                <span class="truncate">{{ interest.label }}</span>
                            </div>
                        </slot>

                        <!-- Remove button for non-permanent interests -->
                        <button
                            v-if="!permanent.includes(interest.value)"
                            type="button"
                            class="ml-2 -mr-1 cursor-pointer px-1 text-gray-500 dark:text-txt-body hover:text-gray-700 dark:hover:text-txt-body transition-colors"
                            @click.stop.prevent="removeInterest(interest)"
                            aria-label="Remove interest"
                        >
                            <CIcon name="close" size="small" />
                        </button>
                    </div>

                    <!-- Input Field -->
                    <div class="flex flex-auto items-center min-w-0">
                        <input
                            v-if="!hasReachedLimit"
                            ref="input"
                            v-model="searchQuery"
                            class="input__nested min-h-10 flex-auto text-ellipsis px-3 border-0 outline-none"
                            :placeholder="effectivePlaceholder"
                            type="text"
                            @focus="handleInputFocus"
                            @blur="handleInputBlur"
                            @input="handleInputChange"
                            @keydown.backspace.stop="handleBackspace"
                            @keydown.down.prevent="navigateDown"
                            @keydown.up.prevent="navigateUp"
                            @keydown.enter.prevent="selectCurrentSuggestion"
                            @keydown.escape.stop="closeDropdown"
                            @keydown.tab="closeDropdown"
                        />
                    </div>

                    <!-- Filter Icon -->
                    <div class="pointer-events-none absolute right-0 top-0 p-3">
                        <CIcon class="pointer-events-none ml-auto mr-1 opacity-50" name="filter-list" />
                    </div>
                </div>
            </div>

            <!-- Dropdown with suggestions -->
            <div
                v-if="showDropdown && filteredSuggestions.length > 0"
                ref="dropdown"
                class="absolute left-0 z-50 mt-1 w-full rounded-lg border border-gray-200 dark:border-default bg-white dark:bg-surface-card shadow-lg"
            >
                <div class="max-h-60 overflow-y-auto py-1">
                    <div
                        v-for="(suggestion, index) in filteredSuggestions"
                        :key="suggestion.value"
                        :data-index="index"
                        class="cursor-pointer px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-subtle transition-colors dark:text-txt-body"
                        :class="{
                            'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300': index === selectedIndex
                        }"
                        @click="handleSuggestionClick(suggestion)"
                        @mouseenter="selectedIndex = index"
                    >
                        <div class="flex items-center gap-2">
                            <CIcon
                                class="shrink-0"
                                :class="
                                    isOptionSelected(suggestion)
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-gray-600 dark:text-txt-body'
                                "
                                :name="isOptionSelected(suggestion) ? 'checkmark' : 'hashtag'"
                                size="small"
                            />
                            <span class="flex-1">{{ suggestion.label }}</span>
                            <span
                                v-if="isOptionSelected(suggestion)"
                                class="text-xs font-medium text-green-600 dark:text-green-400"
                            >
                                Vybrané
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No results message -->
            <div
                v-else-if="showDropdown && searchQuery.trim() && filteredSuggestions.length === 0"
                class="absolute left-0 z-50 mt-1 w-full rounded-lg border border-gray-200 dark:border-default bg-white dark:bg-surface-card shadow-lg py-3"
            >
                <div class="px-4 text-gray-900 dark:text-txt-body">
                    {{
                        noResultsMessage ||
                        canCreateMessage ||
                        'Nenašli sa žiadne kľúčové slová. Stlačením ENTERu vytvoríš nové kľúčové slovo.'
                    }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Option, Extractor } from '@/utils/normalizeOption'
import CIcon from '@/components/ui/CIcon.vue'
import { normalizeOption } from '@/utils/normalizeOption'
import { matchSorter } from 'match-sorter'

interface Props<T = string | number> {
    modelValue?: T[]
    permanent?: T[]
    banned?: T[]
    showSelectedInDropdown?: boolean
    toggleSelectedInDropdown?: boolean
    placeholder?: string
    addMorePlaceholder?: string
    options?: any[]
    optionValueKey?: ((option: T) => string | number) | keyof T
    optionLabelKey?: ((option: T) => string) | keyof T
    limit?: number
    // How many options to show in the dropdown when the search box is empty.
    // Once the user types, all matches are shown regardless. 0 = no cap.
    maxSuggestions?: number
    noResultsMessage?: string
    canCreateMessage?: string
    customValues?: boolean
    delimiters?: string
    addInputOption?: boolean
    disabled?: boolean
    validate?: boolean
}

const props = withDefaults(defineProps<Props<any>>(), {
    modelValue: () => [],
    permanent: () => [],
    banned: () => [],
    showSelectedInDropdown: false,
    toggleSelectedInDropdown: false,
    placeholder: 'Type to search interests...',
    addMorePlaceholder: ' Pridaj ďalšie kľúčové slová ...',
    options: () => [],
    optionValueKey: undefined,
    optionLabelKey: undefined,
    limit: undefined,
    maxSuggestions: 10,
    noResultsMessage: undefined,
    canCreateMessage: undefined,
    customValues: false,
    delimiters: '',
    addInputOption: false,
    disabled: false,
    validate: false
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: (string | number)[]): void
    (e: 'add', data: any): void
    (e: 'remove', data: any): void
    (e: 'query', query: string): void
    (e: 'onCustomInput', value: string): void
}>()

// Reactive state
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedIndex = ref(-1)
const errorMessage = ref('')

// Template refs
const autocompleteContainer = ref<HTMLElement>()
const inputContainer = ref<HTMLElement>()
const input = ref<HTMLInputElement>()
const dropdown = ref<HTMLElement>()

// Computed properties
const normalizedOptions = computed((): Option[] => {
    return props.options?.map((item) =>
        normalizeOption(item, {
            value: props.optionValueKey as Extractor,
            label: props.optionLabelKey as Extractor
        })
    ) || []
})

const modelValueArray = computed((): any[] => {
    return Array.isArray(props.modelValue) ? props.modelValue : []
})

const selectedInterests = computed((): Option[] => {
    return modelValueArray.value.map((value) => {
        const found = normalizedOptions.value.find((option) => option.value === value)
        if (found) return found

        // Fallback for values not in `options` (manually-added IDs, deleted records, etc.).
        // If the stored value is a primitive, render it as-is so the chip displays
        // *something* and the × button has a real value to remove.
        if (typeof value === 'string' || typeof value === 'number') {
            return { value, label: String(value), data: value }
        }

        return normalizeOption(value, {
            value: props.optionValueKey as Extractor,
            label: props.optionLabelKey as Extractor
        })
    })
})

const availableOptions = computed((): Option[] => {
    return normalizedOptions.value.filter((option) => {
        if (props.banned.includes(option.value)) {
            return false
        }

        if (!props.showSelectedInDropdown && modelValueArray.value.includes(option.value)) {
            return false
        }

        return true
    })
})

const filteredSuggestions = computed((): Option[] => {
    const query = searchQuery.value.trim()

    if (!query) {
        return props.maxSuggestions > 0
            ? availableOptions.value.slice(0, props.maxSuggestions)
            : availableOptions.value
    }

    const filtered = matchSorter(availableOptions.value, query, {
        keys: ['label']
    })

    if (shouldUseKeywordValidation.value) {
        return filtered.filter(({ label }) => label.length <= 35 && label.split(' ').length <= 3)
    }

    return filtered
})

const hasReachedLimit = computed((): boolean => {
    return (props.limit ?? 0) > 0 && modelValueArray.value.length >= (props.limit ?? 0)
})

const effectivePlaceholder = computed((): string => {
    if (modelValueArray.value.length === 0) {
        return props.placeholder
    }
    return props.addMorePlaceholder
})

const shouldUseKeywordValidation = computed((): boolean => props.validate && !props.customValues)

const delimiterPattern = computed((): RegExp | null => {
    if (!props.customValues || !props.delimiters) {
        return null
    }

    const escapedDelimiters = props.delimiters.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`[${escapedDelimiters}]+`)
})

const endsWithDelimiterPattern = computed((): RegExp | null => {
    if (!props.customValues || !props.delimiters) {
        return null
    }

    const escapedDelimiters = props.delimiters.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`[${escapedDelimiters}]$`)
})

const isOptionSelected = (option: Option): boolean => {
    return modelValueArray.value.includes(option.value)
}

// Methods
const focusInput = () => {
    if (input.value && !props.disabled) {
        input.value.focus()
    }
}

const handleInputFocus = () => {
    if (!props.disabled) {
        showDropdown.value = true
        selectedIndex.value = -1
    }
}

const handleInputChange = () => {
    errorMessage.value = ''
    showDropdown.value = true
    selectedIndex.value = -1
    consumeDelimitedCustomValues()
    emit('query', searchQuery.value)
}

const handleInputBlur = () => {
    if (props.customValues) {
        createCustomValue(searchQuery.value)
    }
}

const handleBackspace = () => {
    if (!searchQuery.value && selectedInterests.value?.length) {
        const lastInterest = selectedInterests.value[selectedInterests.value.length - 1]
        if (lastInterest && !props.permanent.includes(lastInterest.value)) {
            removeInterest(lastInterest)
        }
    }
}

const navigateDown = () => {
    if (!showDropdown.value) {
        showDropdown.value = true
        return
    }
    
    selectedIndex.value = Math.min(filteredSuggestions.value.length - 1, selectedIndex.value + 1)
    scrollToSelectedItem()
}

const navigateUp = () => {
    selectedIndex.value = Math.max(-1, selectedIndex.value - 1)
    scrollToSelectedItem()
}

const scrollToSelectedItem = () => {
    nextTick(() => {
        const selectedItem = dropdown.value?.querySelector(`[data-index="${selectedIndex.value}"]`)
        selectedItem?.scrollIntoView({ block: 'nearest' })
    })
}

const selectCurrentSuggestion = () => {
    const selectedSuggestion = filteredSuggestions.value[selectedIndex.value]

    if (selectedIndex.value >= 0 && selectedSuggestion) {
        // User explicitly selected a suggestion with arrow keys
        handleSuggestionClick(selectedSuggestion)
    } else if (searchQuery.value.trim()) {
        // User typed something but didn't navigate to a suggestion
        // Check if there's an exact match (case insensitive)
        const exactMatch = filteredSuggestions.value.find(
            (suggestion) => suggestion.label.toLowerCase() === searchQuery.value.trim().toLowerCase()
        )

        if (exactMatch) {
            // Only use existing suggestion if it's an exact match
            handleSuggestionClick(exactMatch)
        } else {
            // Create new keyword from the search query
            createNewKeyword(searchQuery.value.trim())
        }
    }
}

const handleSuggestionClick = (suggestion: Option) => {
    if (props.toggleSelectedInDropdown && isOptionSelected(suggestion)) {
        removeInterest(suggestion)
        return
    }

    selectSuggestion(suggestion)
}

const createNewKeyword = (keyword: string) => {
    if (hasReachedLimit.value) {
        return
    }

    if (props.customValues) {
        createCustomValue(keyword)
        return
    }

    // Validate keyword if validation is enabled
    if (shouldUseKeywordValidation.value && keyword.length > 35) {
        errorMessage.value = 'Interest name is too long (max 35 characters)'
        return
    }

    if (shouldUseKeywordValidation.value && keyword.split(' ').length > 3) {
        errorMessage.value = 'Interest can contain max 3 words'
        return
    }

    // Check if keyword already exists
    if (modelValueArray.value.includes(keyword)) {
        return
    }

    // Create new option and add it
    const newOption: Option = normalizeOption(keyword, {
        value: props.optionValueKey as Extractor,
        label: props.optionLabelKey as Extractor
    })

    const newValue = [...modelValueArray.value, newOption.value]
    emit('update:modelValue', newValue)
    emit('add', newOption.data)

    // Clear search and close dropdown
    searchQuery.value = ''
    closeDropdown()

    // Refocus input for next selection
    nextTick(() => {
        focusInput()
    })
}

const selectSuggestion = (suggestion: Option) => {
    if (hasReachedLimit.value) {
        return
    }

    if (shouldUseKeywordValidation.value && suggestion.label.length > 35) {
        errorMessage.value = 'Interest name is too long (max 35 characters)'
        return
    }

    if (shouldUseKeywordValidation.value && suggestion.label.split(' ').length > 3) {
        errorMessage.value = 'Interest can contain max 3 words'
        return
    }

    if (modelValueArray.value.includes(suggestion.value)) {
        return
    }

    const newValue = [...modelValueArray.value, suggestion.value]
    emit('update:modelValue', newValue)
    emit('add', suggestion.data)

    // Clear search and close dropdown
    searchQuery.value = ''
    closeDropdown()

    // Refocus input for next selection
    nextTick(() => {
        focusInput()
    })
}

const removeInterest = (interest: Option) => {
    const newValue = modelValueArray.value.filter((value) => value !== interest.value)
    emit('update:modelValue', newValue)
    emit('remove', interest.data)

    nextTick(() => {
        focusInput()
    })
}

const createCustomValue = (rawValue: string) => {
    const value = rawValue.trim()
    if (!value) {
        return
    }

    emit('onCustomInput', value)
    searchQuery.value = ''
    closeDropdown()

    nextTick(() => {
        focusInput()
    })
}

const consumeDelimitedCustomValues = () => {
    const pattern = delimiterPattern.value
    if (!props.customValues || !pattern || !pattern.test(searchQuery.value)) {
        return
    }

    const endsWithDelimiter = Boolean(endsWithDelimiterPattern.value?.test(searchQuery.value))
    const parts = searchQuery.value
        .split(pattern)
        .map((part) => part.trim())
        .filter(Boolean)

    const pendingValue = endsWithDelimiter ? '' : parts.pop() || ''
    parts.forEach(createCustomValue)
    searchQuery.value = pendingValue
}

const closeDropdown = () => {
    showDropdown.value = false
    selectedIndex.value = -1
}

const handleClickOutside = (e: MouseEvent) => {
    if (showDropdown.value && !autocompleteContainer.value?.contains(e.target as Node)) {
        closeDropdown()
    }
}

// Lifecycle
onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside)
})

watch(
    () => props.addInputOption,
    (shouldAdd) => {
        if (shouldAdd && props.customValues) {
            createCustomValue(searchQuery.value)
        }
    },
    { flush: 'sync' }
)
</script>
